// components/SSOLoginModal.tsx
import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Message } from 'semantic-ui-react';
import { tenantService } from '../services/TenantService';
import { useHybridAuth } from '../auth/HybridAuthContext';
import { RequestPayload } from '../services/ApiClient/types';
import { VerifyTenantDomainRequest, TenantConfiguration, TenantVerificationResponse } from '../services/TenantService/types';

interface SSOLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SSOLoginModal: React.FC<SSOLoginModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const { login } = useHybridAuth();

  // reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setError(null);
      setEmailError(null);
    }
  }, [isOpen]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const extractDomain = (email: string): string => {
    return email.split('@')[1];
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    // Clear previous errors
    setEmailError(null);
    setError(null);

    // Validate email as user types
    if (newEmail && !validateEmail(newEmail)) {
      setEmailError('Please enter a valid email address');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    
    setError(null);
    setEmailError(null);

    // Final email validation
    if (!email) {
      setEmailError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }    
    setIsLoading(true);
    const domain = extractDomain(email);
    
    try {
      //console.log(`Verifying domain: ${domain} for email: ${email}`);
      let request: VerifyTenantDomainRequest = { domain: domain }
      let payload: RequestPayload<VerifyTenantDomainRequest> = { payload: request }
      const response = await tenantService.verifyDomain(payload);
      
      if (!response.result.hasSSO) {
        setError(response.message || 'SSO is not configured for this domain');
        return;
      }

      if (response.result.configuration) {
        await login(response.result.configuration);        
        onClose();
      }
    } catch (err) {
      setError('Failed to verify domain. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      size="tiny"
      open={isOpen}
      onClose={onClose}
      closeIcon
    >
      <Modal.Header>Sign In with SSO</Modal.Header>
      <Modal.Content>
        <Form onSubmit={handleSubmit} error={!!error}>
          <Form.Field>
            <label>Email Address</label>
            <input
              type="email"
              placeholder="Enter your work email"
              value={email}
              onChange={handleEmailChange}
              required
            />
            {emailError && (
              <Message
                error content ={emailError}
                />
            )}
          </Form.Field>
          {error && (
            <Message
              error
              content={error}
            />
          )}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          positive
          loading={isLoading}
          disabled={isLoading || !email || !!emailError}
          onClick={handleSubmit}
        >
          Continue
        </Button>
      </Modal.Actions>
    </Modal>
  );
};